import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {CacheObjectService, DeviceService, EventTrackerService} from '@app/core';
import {ScrollToLastPosition} from '@app/helper/scroll-to-last-position';
import {ProductTypeEnum} from '@app/interfaces/iBook';
import {IndicatorService} from '@app/shared';
import {environment} from '@env/environment';
import {Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';

import {CatalogService} from '../catalog.service';

@Component({
  templateUrl: './catalog-list.component.html',
  styleUrls: ['./catalog-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class CatalogListComponent extends ScrollToLastPosition implements OnInit, OnDestroy {

  @ViewChild('containerHref', {read: ElementRef}) private hrefTags: ElementRef<any>;
  public readonly PAGE_SIZE = 20;
  public title: string;
  public books: Array<any> = [];
  public _environment = environment;
  public isLoading: boolean = false;
  public isEnd: boolean = false;
  public description: any;
  public subscription: Subscription = new Subscription();

  //  'price' | 'star' | 'menu' | 'none'
  public type: string;
  private listKey: string;

  public get pageNo(): number {
    return this.cache.pageNumer;
  }

  public set pageNo(v: number) {
    this.cache.pageNumer = v;
  }

  constructor(
    public readonly device: DeviceService,
    public readonly _dom: DomSanitizer,
    private readonly indicator: IndicatorService,
    private readonly catalogService: CatalogService,
    private readonly route: ActivatedRoute,
    private readonly cache: CacheObjectService<any>,
    private readonly chkd: ChangeDetectorRef,
    private readonly router: Router,
    private readonly eventTracker: EventTrackerService) {
    super(router);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.restoredState)
          this.catalogService.isBackFromItem = true;
      });
  }

  public ngOnInit() {
    this.subscription = this.route.queryParamMap.subscribe(_query => {
      this.pageNo = 0;
      this.indicator.show();
      this.title = _query.get('title');
      if (_query.get('listKey')?.indexOf("/") === 0)
        this.listKey = _query.get('listKey')?.substring(1, (_query.get('listKey')?.length));
      else
        this.listKey = _query.get('listKey');
      this.type = _query.get('type');
      if (_query.has('showPrice'))
        this.type = _query.get('showPrice') === "true" ? 'price' : 'none';
      this.loadDescription(_query.get('description'));
      if (this.route.snapshot.paramMap.get('title'))
        this.title = this.route.snapshot.paramMap.get('title');
      this.loadBookListNext();
    });
  }

  public goToBook(id, idx: number) {
    this.indicator.show();
    this.eventTracker.logEvent('x_list_item_click', {"x_list_title": this.title, "x_item_index": idx, "x_item_id": id});
    this.router.navigate(['/audiobook', id]);
  }

  public async loadBookListNext() {
    const url = `/${this.listKey}?$inlinecount=allpages&$skip=${this.pageNo * this.PAGE_SIZE}&$top=${this.PAGE_SIZE}`;
    this.isLoading = true;
    await this.cache.getObjectCacheOfList(url, this.listKey, this.pageNo).pipe(
      map((res) => {
        this.isLoading = false;
        this.isEnd = (res.items.length === res.total);
        this.books = res.items;
        this.indicator.hide();
        this.indicator.hideHude();
        this.chkd.detectChanges();
        return res;
      })
    ).toPromise();
    if (this.catalogService.isBackFromItem)
      super.scrollToPosition();
  }

  public goToPage(page: number) {
    this.pageNo = page;
    this.catalogService.isBackFromItem = false;
    this.loadBookListNext();
  }

  public loadDescription(deskKey) {
    if (deskKey) {
      this.cache.getObjectCache(`/api/audiobooks/catalog-type/${deskKey}`).subscribe(x => {
        this.description = x.description;
        this.chkd.detectChanges();
        this.appendEventToImage();
      })
    }
  }

  public appendEventToImage() {
    if (this.hrefTags?.nativeElement) {
      let elements = this.hrefTags?.nativeElement?.getElementsByTagName('a');
      for (let index = 0; index < elements.length; index++) {
        let element = elements[index];
        element.addEventListener('click', this.onClick.bind(this));
      }
    }
  }

  public onClick(event: Event) {
    event.preventDefault();
    //  // Genre
    // let tmp = link.split('/');
    // link = [`/category/${tmp[tmp.length - 1]}`];
    const el: HTMLElement = event.currentTarget as HTMLElement;
    const type = el.getAttribute('data-type');
    const item = el.getAttribute('data-item');
    const link = el.getAttribute('href');
    switch (type?.toLowerCase()) {
      case '5':
      case 'genre':
        const titleGenre = el.getAttribute('data-title');
        const contentType = el.getAttribute('data-content-type');
        if (contentType) {
          if (ProductTypeEnum[contentType] === ProductTypeEnum.Podcast || ProductTypeEnum[contentType] === ProductTypeEnum.AudioSeries) {
            this.router.navigate([`/clp/listItem/${item}`], {
              queryParams: {titleGenre, contentType: ProductTypeEnum[contentType]},
            });
          } else {
            this.router.navigate(["/clp", item], {
              queryParams: {titleGenre, contentType: ProductTypeEnum[contentType]},
            });
          }
        } else {
          this.router.navigate(["/clp", item], {
            queryParams: {titleGenre},
          });
        }
        break;
      case '4':
      case 'landingpage':
        this.router.navigate(['/webview-page'], {queryParams: {link}});
        break;
      case '3':
      case 'audiobook':
        this.router.navigate(['/audiobook', parseInt(item)]);
        break;
      case '2':
      case 'subscripotion':
        this.router.navigate(['/subscription/plans']);
        break;
      case '1':
      case 'catalog':
        const showPrice = el.getAttribute('data-showPrice');
        const title = el.getAttribute('data-title');
        const description = el.getAttribute('data-description');
        this.router.navigate(['/catalog'], {
          queryParams: {
            listKey: `api/audiobooks/list/${item}`,
            showPrice: showPrice ? showPrice : false,
            description: description ? description : '',
            title: title ? title : ''
          },
          relativeTo: null,
          replaceUrl: false
        });
        break;
      default:
        if (link) {
          // if a URL string is absolute or relative?
          const r = new RegExp('^(?:[a-z+]+:)?//', 'i');
          if (r.test(link)) {
            this.router.navigate(['/webview-page'], {queryParams: {link}});
          } else {
            this.router.navigate([link]);
          }
        }
        break;
    }
  }

  public getDescriptionHeight(): number {
    return (this.hrefTags?.nativeElement?.getBoundingClientRect()?.height - (!!this.description ? 30 : 0));
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
    this.indicator.show();
    this.indicator.hideHude();
  }
}
